import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { MediaMatcher } from '@angular/cdk/layout';
import { Subject, forkJoin, map } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { MatSidenav } from '@angular/material/sidenav';
import { EnvironmentService } from './services/environment.service';
import { Environment } from './models/environment';
import { UserProfileService } from './services/user-profile.service';
import { ToastService } from './services/toast.service';
import { AdoUserProfile } from './models/ado-user-profile';
import { OwnershipEntry } from './models/ownership-entry';
import { TeamOwnerMinorEntry } from './models/team-owner-minor-entry';
import { GitHubAuthService } from './services/github-auth.service';
import { GitHubApiService } from './services/github-api.service';
import { datadogRum } from '@datadog/browser-rum';

// Template

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  mobileQuery: MediaQueryList;
  title = 'platform-portal';
  isAuthenticated: boolean = false;
  userName: string = '';
  loginDisplay: boolean = false;
  isOnCallModule: boolean = false;
  onCallEnabled: boolean = true;
  environment: Environment;
  mdpUrl: string;
  graphUrl: string;
  userProfile?: AdoUserProfile | null;
  membershipData: any = {
    adoGroups: [],
    adoTeams: [],
    adGroups: []
  };
  ownershipData: any = {
    repoOwnership: [] as OwnershipEntry[],
    teamOwnership: [] as TeamOwnerMinorEntry[]
  };
  private readonly _destroying$ = new Subject<void>();
  private mobileQueryListener: () => void;
  currentRoute: string = '';
  @ViewChild('sidenav') sidenav!: MatSidenav;

  // Add a BehaviorSubject to track profile initialization
  profileInitialized$ = this.userProfileService.profileInitialized$;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private media: MediaMatcher,
    private router: Router,
    private environmentService: EnvironmentService,
    private readonly userProfileService: UserProfileService,
    private readonly toast: ToastService,
    private readonly githubAuth: GitHubAuthService,
    private readonly githubApi: GitHubApiService
  ) {
    this.mobileQuery = this.media.matchMedia('(max-width: 800px)');
    this.mobileQueryListener = () => this.changeDetectorRef.detectChanges();
    this.mobileQuery.addEventListener('change', this.mobileQueryListener);
    this.environment = this.environmentService.getEnvironment();
    this.onCallEnabled = this.environment.featureToggles.onCallEnabled === 'true';
    this.mdpUrl = this.environment.mdpUrl;
    this.graphUrl = this.environment.graphUrl || '';
  }

  ngOnInit(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentRoute = event.url;
        this.isOnCallModule = this.currentRoute.includes('/oncall');

        // Open sidenav for root route (/) or oncall routes
        if (this.currentRoute === '/' || this.isOnCallModule) {
          this.openSidenav();
        } else {
          // Close sidenav for all other routes
          this.closeSidenav();
        }
      }
    });

    // Subscribe to authentication state from UserProfileService
    this.userProfileService.isAuthenticated()
      .pipe(takeUntil(this._destroying$))
      .subscribe(isAuthenticated => {
        this.isAuthenticated = isAuthenticated;
        this.loginDisplay = isAuthenticated;

        if (!isAuthenticated) {
          this.userName = '';
          this.membershipData = {
            adoGroups: [],
            adoTeams: [],
            adGroups: []
          };
          this.ownershipData = {
            repoOwnership: [],
            teamOwnership: []
          };
          return;
        }

        // Update user name when authenticated
        this.userProfileService.getUserName()
          .pipe(takeUntil(this._destroying$))
          .subscribe(name => {
            this.userName = name;

            // Set user in Datadog RUM when authenticated
            if (name && this.userProfile) {
              datadogRum.setUser({
                id: this.userProfile.id,
                name: name,
                email: this.userProfile.emailAddress
              });
            }
          });

        // Get current profile when authenticated
        this.userProfileService.getCurrentProfile()
          .pipe(
            takeUntil(this._destroying$),
            switchMap(profile => {
              this.userProfile = profile;
              // Load both membership and ownership data
              return this.loadUserData();
            })
          )
          .subscribe({
            next: (userData) => {
              this.membershipData = userData.membershipData;
              this.ownershipData = userData.ownershipData;
              console.log('User data loaded successfully');
            },
            error: (error) => {
              console.error('Error loading user data:', error);
              this.toast.error('Failed to load user data');
            }
          });
      });

    // Initialize authentication via UserProfileService
    this.userProfileService.initializeAuthentication()
      .pipe(takeUntil(this._destroying$))
      .subscribe({
        next: () => {
          // Initialize GitHub authentication after MSAL authentication is complete
          this.initializeGitHubAuthentication();
        },
        error: (error) => {
          console.error('Error in authentication initialization:', error);
          this.toast.error('Error initializing application');
        }
      });
  }

  /**
   * Loads all user-related data using existing UserProfileService methods
   * @returns Combined user data as an observable
   */
  private loadUserData() {
    return forkJoin({
      membershipData: this.loadMembershipData(),
      ownershipData: this.loadOwnershipData()
    });
  }

  /**
   * Loads membership data using existing UserProfileService methods
   * @returns Combined membership data as an observable
   */
  private loadMembershipData() {
    // Get ADO groups and teams using the public methods
    return forkJoin({
      adoMembership: this.userProfileService.getAzureDevOpsMembershipData(this.environment.github.org),
      adGroups: this.userProfileService.getCarvanaADGroups(this.environment.github.org)
    }).pipe(
      map(result => ({
        adoGroups: result.adoMembership.groups,
        adoTeams: result.adoMembership.teams,
        adGroups: result.adGroups
      }))
    );
  }

  /**
   * Loads ownership data using existing UserProfileService methods
   * @returns Combined ownership data as an observable
   */
  private loadOwnershipData() {
    return forkJoin({
      repoOwnership: this.userProfileService.getRepoOwnershipMaster(this.environment.defaultOrg),
      teamOwnership: this.userProfileService.getTeamOwnerMinor(this.environment.defaultOrg)
    });
  }

  async closeSidenav(): Promise<void> {
    if (this.sidenav) {
      await this.sidenav.close();
    }
  }

  async openSidenav(): Promise<void> {
    if (this.sidenav) {
      await this.sidenav.open();
    }
  }

  loginRedirect() {
    this.userProfileService.login();
  }

  logout() {
    this.userProfileService.logout();
  }

  /**
   * Initializes GitHub authentication automatically when MSAL auth is complete
   */
  private initializeGitHubAuthentication(): void {
    // Check if user is already connected to GitHub
    if (this.githubAuth.isConnected()) {
      console.log('User already connected to GitHub');
      return;
    }

    // Check if there's a stored token from previous session
    const hasStoredToken = this.githubAuth.getAccessToken();
    if (hasStoredToken) {
      // Try to refresh user data with stored token
      this.githubApi.refreshUserData()
        .pipe(takeUntil(this._destroying$))
        .subscribe({
          next: () => {
            console.log('GitHub authentication restored from stored token');
          },
          error: (error) => {
            console.log('Stored token invalid, clearing and will need fresh authentication');
            this.githubAuth.logout(); // Clear invalid token
            this.attemptAutoGitHubLogin();
          }
        });
      return;
    }

    // Wait a moment for MSAL authentication to fully complete, then attempt login
    setTimeout(() => {
      this.attemptAutoGitHubLogin();
    }, 1000); // 1 second delay to ensure MSAL is fully initialized
  }

  /**
   * Attempts automatic GitHub login if conditions are met
   */
  private attemptAutoGitHubLogin(): void {
    if (this.isAuthenticated && !this.githubAuth.isConnected()) {
      console.log('Initiating automatic GitHub authentication...');
      this.githubAuth.loginWithGitHub()
        .pipe(takeUntil(this._destroying$))
        .subscribe({
          next: () => {
            // OAuth flow initiated successfully
            console.log('GitHub OAuth flow initiated');
          },
          error: (error) => {
            if (error.isPopupBlocked) {
              // Log popup blocker detection but don't show dialog for automatic attempts
              console.warn('GitHub authentication popup was blocked during automatic initialization. User can manually connect from their profile page.');
            } else {
              console.error('Automatic GitHub authentication failed:', error);
            }
            // Don't show error toast for automatic initialization failures
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeEventListener('change', this.mobileQueryListener);
    this._destroying$.next();
    this._destroying$.complete();
  }
}
